import { HistoryBasedValueRequest } from '@ltvco/refresh-lib/api';
import {
  AppConfig,
  HiddenSectionsProvider,
  LLMProvider,
  useAppConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { usePublicReceipts, useReport } from '@ltvco/refresh-lib/hooks';
import {
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
import { Box, Grid, useTheme } from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  isZeroed,
  useScreenSize,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  useAccount,
  DebugMenu,
  EtagCTA,
  generateVehicleSpecs,
  getPremiumRecordsFound,
  HideableSection,
  NotesSection,
  ReportLoading,
  ReportRating,
  UpgradePremiumDataModal,
  useFreeUpgradeMutation,
  UserReportPreferencesModal,
  VehicleReport,
  VehicleReportDisclaimer,
  useVehicleValuation,
  updateSetting,
} from '@ltvco/refresh-lib/v1';
import {
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportActionsWithDateUpdated,
  ReportGeneratingScreen,
  ReportNavigationMenuProps,
  VehicleDetailedHistorySection,
  LizanoListItemDataVehicle,
  VehicleDamageHistorySection,
  VehicleTitleSummarySection,
  VehicleSmartInsightsSection,
  VehicleSalesSection,
  VehicleReportOverview,
  VehicleSpecsSection,
  VehicleMaintenanceScheduleSection,
  VehicleOwnershipCostSection,
  VehicleAuctionsSection,
  VehicleRecallsSection,
  VehicleSafetyRatingsSection,
  VehicleServiceRepairsSection,
  VehicleTheftsSection,
  VehicleWarrantySection,
  VehicleOwnershipHistorySection,
  VehicleSafetyComplaintsSection,
  VehicleInspectionsSection,
  VehicleTrimSelect,
  GetMostOutSection,
  ReportNullState,
  RecordsFoundModal,
  PartialVINModal,
  VehicleExportsSection,
  SocialSentiment,
  CommunityBuzz,
  statesContact,
  vehicleComplaints,
  ReportChangesOverview,
} from '@ltvco/refresh-lib/v2';
import {
  useQuery,
  useFeatureIsOn,
  useFeatureValue,
} from '@ltvco/refresh-lib/vendors';
import { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { VehicleValueSection } from 'components/VehicleValueSection/VehicleValueSection';
import { getVehicleNavLinkDataV2 } from 'navLinkData/vehicleNavLinkData';

import { getInitialVehicleReportPreferences } from './Preferences/getInitialVehicleReportPreferences';

import type { ReportRouteProps } from './types';

interface VehicleReportProps extends ReportRouteProps {
  onInvalidVin?: (vin: string) => void;
}

// START CONSTANTS
// `bump-4464` forces all users into the V1 (control) experience. A user who
// was bucketed into the variation and hid the merged `accidents_and_damages`
// section should still see both V1 leaves it was merged from hidden.
const V1_ACCIDENTS_AND_DAMAGES_LEAVES = [
  'accidents_reported',
  'damage_reported',
];
// END CONSTANTS

const UseVehicleComplaints = (year: string, make: string, model: string) => {
  const { logError } = useContext(AppConfig);
  const result = useQuery<any>({
    queryKey: ['vehicleComplaints'],
    queryFn: () => {
      return vehicleComplaints(year, make, model);
    },

    enabled: Boolean(year) && Boolean(make) && Boolean(model),
    onError: (error) => {
      logError('Error fetching vehicle complaints', error as Error);
    },
  });

  return { ...result };
};

const UseStatesContact = () => {
  const { logError } = useContext(AppConfig);
  const result = useQuery<any>({
    queryKey: ['statesContact'],
    queryFn: () => {
      return statesContact();
    },

    onError: (error) => {
      logError('Error fetching vehicle complaints', error as Error);
    },
  });

  return { ...result };
};

export function VehicleReportPage({
  permalink,
  firstReportScreen,
  onInvalidVin,
}: VehicleReportProps) {
  const queryResult = useReport(permalink);
  const { reportIsMonitored } = queryResult;

  const { isMobile } = useScreenSize();
  const theme = useTheme();

  // AB Tests
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const [, triggerUpdate] = useState(0);

  const { data: accountData } = useAccount(true);
  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false);
  const initialVehicleReportPreferences = getInitialVehicleReportPreferences();

  const [sectionsToHide, setSectionsToHide] = useState(
    initialVehicleReportPreferences
  );

  const hiddenSections =
    accountData?.account?.user_report_preferences.hidden_sections
      .vehicle_report || [];

  useEffect(() => {
    if (hiddenSections) {
      setSectionsToHide((prevSections) =>
        prevSections.map((navSection) => ({
          ...navSection,
          sections: navSection.sections.map((section) => ({
            ...section,
            checked: V1_ACCIDENTS_AND_DAMAGES_LEAVES.includes(
              section.nameToRecord
            )
              ? !hiddenSections.includes(section.nameToRecord) &&
                !hiddenSections.includes('accidents_and_damages')
              : !hiddenSections.includes(section.nameToRecord),
          })),
        }))
      );
    }
  }, [accountData]);

  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const zeroed = isZeroed(queryResult);
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [showAdditionalRecordsModal, setShowAdditionalRecordsModal] =
    useState(false);
  const [premiumRecordsFound, setPremiumRecordsFound] = useState<string[]>([]);
  const [upgradeReportCalled, setUpgradeReportCalled] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [showPartialVINModal, setShowPartialVINModal] = useState(false);

  const { data } = UseVehicleComplaints(year, make, model);
  const upgradeReport = useFreeUpgradeMutation(permalink);
  const { data: statesContactInfo } = UseStatesContact();
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);

  const report = useMemo(() => {
    if (
      !queryResult.isLoading &&
      !queryResult.isError &&
      queryResult.data &&
      !queryResult.isFetching
    ) {
      const newReport = new VehicleReport(queryResult.data);
      const reportData = newReport.data;

      if (reportData.automobiles) {
        const newYear = reportData.automobiles[0]?.year?.toString() || '';
        const newMake = reportData.automobiles[0]?.make || '';
        const newModel = reportData.automobiles[0]?.model || '';
        const newReportTitle =
          reportData.automobiles[0]?.vehicleYMM?.toUpperCase() ||
          reportData.meta?.metaName ||
          '';

        if (newYear !== year) {
          setYear(newYear);
        }
        if (newMake !== make) {
          setMake(newMake);
        }
        if (newModel !== model) {
          setModel(newModel);
        }
        if (newReportTitle !== reportTitle) {
          setReportTitle(newReportTitle);
        }

        document.title = `${newReportTitle || 'Search'} - Beenverified`;
      }

      return newReport;
    }

    return null;
  }, [queryResult, year, make, model, reportTitle]);
  const {
    publicReceipts,
    isPublicReceiptsLoading,
    hasMoreReceipts,
    isFetchingNextReceipts,
    fetchNextReceiptsPage,
  } = usePublicReceipts({
    vin: report?.data?.meta?.search_data?.vin,
    licensePlate: report?.data?.meta?.search_data?.license_plate,
    state: report?.data?.meta?.search_data?.state,
  });

  const vehicleNavLinkDataV2 = getVehicleNavLinkDataV2(
    report?.data || null,
    data?.complaints?.length || 0,
    theme,
    publicReceipts?.length || 0
  );

  const currentVehicle = report?.data?.automobiles?.[0];

  const valuationRequestData = {
    year: currentVehicle?.year?.toString() ?? '',
    make: currentVehicle?.make ?? '',
    model: currentVehicle?.model ?? '',
    mileage: currentVehicle?.mileage?.miles ?? 0,
    location: (() => {
      const city = currentVehicle?.city ?? currentVehicle?.origin?.city ?? '';
      const state = currentVehicle?.state ?? '';
      const zip = currentVehicle?.zip ?? '';

      const parts = [city, state, zip].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '';
    })(),
  };

  const { logError } = useAppConfigContext();

  const hasSkippedCommunityBuzzModal =
    accountData?.account?.user_settings?.hide_community_buzz_modal;

  const handleCommunityBuzzModalShown = useCallback(async () => {
    if (hasSkippedCommunityBuzzModal) return;

    try {
      await updateSetting({
        setting: 'hide_community_buzz_modal',
        state: true,
      });
    } catch (error) {
      logError('Error updating user setting', error as Error);
    }
  }, [hasSkippedCommunityBuzzModal, logError]);

  const isValidValuationRequest = (
    data: typeof valuationRequestData
  ): boolean => {
    return (
      Boolean(data.year) &&
      Boolean(data.make) &&
      Boolean(data.model) &&
      data.mileage > 0
    );
  };

  const { data: fetchedValuation } = useVehicleValuation(valuationRequestData, {
    enabled: isValidValuationRequest(valuationRequestData),
  });

  // Check for partial VIN and show modal if needed
  useEffect(() => {
    const vin = report?.data?.meta?.search_data?.vin;
    const isPartialVIN = vin && vin.length === 14 && Number(year) >= 1981;

    if (isPartialVIN) {
      setShowPartialVINModal(true);
    }
  }, [report?.data?.meta?.search_data?.vin, year]);

  const handleClosePartialVINModal = useCallback(() => {
    setShowPartialVINModal(false);
  }, []);

  useEffect(() => {
    const searchedVin = report?.data?.meta?.search_data?.vin;
    const isZeroedReport = isZeroed(queryResult);
    const isEmpty = report?.data?.automobiles?.length === 0;
    if ((isZeroedReport || isEmpty) && searchedVin) {
      onInvalidVin?.(searchedVin);
    }
  }, [report, queryResult, onInvalidVin]);

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="vehicle"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={10} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  if (!report && (upgradeReport.isSuccess || upgradeReport.isLoading)) {
    return <UpgradePremiumDataModal />;
  }

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }
  const reportData = report.data;
  const trim =
    currentVehicle?.design?.trim ||
    currentVehicle?.trim ||
    reportData?.meta?.search_data?.trim ||
    'Base';
  if (reportData.automobiles.length === 0) {
    return <ReportNullState />;
  }

  const vehicleSpecs = generateVehicleSpecs(reportData);

  const isYMMSearch = reportData.meta?.search_by === 'ymm';
  const ymmPresent = [year, make, model].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const ymm = ymmPresent.join(' ');
  const reportSubtitle = !isYMMSearch ? reportData.meta?.metaName : '';

  const handleFreeUpgrade = () => {
    upgradeReport.mutate({ permalink });
    setUpgradeReportCalled(true);
  };

  const handleOnClickPremiumData = () => {
    setShowAdditionalRecordsModal(!showAdditionalRecordsModal);
    setUpgradeReportCalled(!upgradeReportCalled);
  };

  const handlePreferencesModalVisibility = () => {
    setPreferencesModalVisible(!preferencesModalVisible);
  };

  if (
    report &&
    upgradeReportCalled &&
    upgradeReport.isSuccess &&
    !showAdditionalRecordsModal
  ) {
    setPremiumRecordsFound(getPremiumRecordsFound(reportData));
    setShowAdditionalRecordsModal(true);
  }

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataVehicle = {
      permalink,
      year: year || '',
      make: make || '',
      model: model || '',
      market_value:
        reportData?.automobiles?.[0]?.valuation?.clean_retail?.adjusted || 0,
    };

    return data;
  };

  const damageHistoryProps = {
    vehicleAccidentsList: reportData?.automobiles?.[0]?.accidents,
    vehicleRecallsList: reportData?.automobiles?.[0]?.recalls,
    vehicleServiceRepairsList: reportData?.automobiles?.[0]?.service_repairs,
    vehicleTheftsList: reportData?.automobiles?.[0]?.thefts,
    vehicleLiensList: reportData?.automobiles?.[0]?.liens,
    vehicleImpoundsList: reportData?.automobiles?.[0]?.impounds,
    vehicleTitleBrandsList: reportData?.automobiles?.[0]?.title_checks,
    vehicleTitleRecordsList: reportData?.automobiles?.[0]?.title_registrations,
    vehicleAuctionsDamageList:
      reportData?.automobiles?.[0]?.damage_history?.reported_damage,
    vehicleSalvagesList: reportData?.automobiles?.[0]?.salvage,
    vehicleRepairQuotesList:
      reportData?.automobiles?.[0]?.damage_history?.repair_quotes,
    nmvtisSuccess: reportData?.automobiles?.[0]?.nmvtis_success || false,
    reportUpgraded: reportData.report_upgraded,
    handleFreeUpgrade,
  };

  const historyBasedValueParams: HistoryBasedValueRequest = {
    year: currentVehicle?.year?.toString() ?? '',
    make: currentVehicle?.make ?? '',
    model: currentVehicle?.model ?? '',
    accidents: currentVehicle?.accidentsCount ?? 0,
    damages: currentVehicle?.damageHistoryCount ?? 0,
    owners: currentVehicle?.ownershipHistoryCount ?? 0,
    recalls: currentVehicle?.recallsCount ?? 0,
    salvages: Boolean(currentVehicle?.salvagesCount),
    thefts: currentVehicle?.theftsCount ?? 0,
    title_brands: currentVehicle?.titleChecksCount ?? 0,
    market_value: fetchedValuation?.clean_retail?.adjusted ?? 0,
  };

  return (
    <HiddenSectionsProvider reportType="vehicle_report">
      <LLMProvider
        llmData={currentVehicle}
        historyBasedValueParams={historyBasedValueParams}
        llmType="vehicle"
      >
        {upgradeReport.isLoading && <UpgradePremiumDataModal />}
        <RecordsFoundModal
          recordsFound={premiumRecordsFound}
          open={showAdditionalRecordsModal}
          handleClose={handleOnClickPremiumData}
        />
        <PartialVINModal
          isOpen={showPartialVINModal}
          onClose={handleClosePartialVINModal}
        />
        <Grid container direction={'row'} columns={12} spacing={7}>
          <Grid
            size={{ sm: 12, md: 4, lg: 4 }}
            sx={{
              display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
            }}
          >
            <ReportNavigationMenu
              reportType="Vehicle Report"
              headerTitle={reportTitle || reportSubtitle || ''}
              subheaderTitle={reportSubtitle || ''}
              menuItems={
                vehicleNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
              }
              customTitleStyle={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                color: '#0A0B0D',
              }}
            />
          </Grid>
          <Grid
            size={{ sm: 12, md: 8, lg: 8 }}
            marginTop={0.5}
            sx={{
              '&.MuiGrid-item': {
                paddingTop: { xs: 3.75, md: 7 },
              },
            }}
          >
            {uncancelFeatureIsOn &&
              uncancelFeatureValue === uncancelBillingVariation2Value && (
                <ReactivationDaysLeft
                  direction="row"
                  isInReport
                  customPadding={'16px 0 16px 0px'}
                />
              )}

            {reportIsMonitored && (
              <ReportChangesOverview
                permalink={permalink}
                navLinkData={vehicleNavLinkDataV2}
              />
            )}

            <>
              <Box
                sx={{
                  height: 30,
                  backgroundColor: '#f8f8f8',
                  position: 'sticky',
                  marginBottom: '-10px',
                  marginX: -1,
                  top: 52,
                  zIndex: 10,
                  display: {
                    xs: 'none',
                    sm: 'block',
                    md: 'block',
                    lg: 'block',
                  },
                }}
              />

              <ReportActionsWithDateUpdated
                reportTitle="Vehicle"
                reportType="vehicle"
                dateUpdated={reportUpdateDate}
                reportUpgraded={false}
                reportDataForListV2={
                  isInListV2Variation ? buildReportDataForListV2() : undefined
                }
              />
            </>

            {isYMMSearch && (
              <VehicleTrimSelect year={year} make={make} model={model} />
            )}
            <VehicleReportOverview
              reportType="vehicle-report"
              vehicleReportData={reportData}
              permalink={permalink}
              nmvtisSuccess={reportData.automobiles[0]?.nmvtis_success}
              reportUpgraded={reportData.report_upgraded}
              handleFreeUpgrade={handleFreeUpgrade}
              hasBadge={false}
            />
            {!isYMMSearch && hasAllYMMPresent && (
              <HideableSection
                nameToDisplay="Smart Insights"
                nameToRecord="smart_insights"
                id="smart-insights"
              >
                <Box mb={2.5}>
                  <VehicleSmartInsightsSection reportData={reportData} />
                </Box>
              </HideableSection>
            )}
            <>
              {!isYMMSearch && (
                <>
                  <GetMostOutSection
                    sectionId="get-most-out"
                    claimed={reportData.meta?.tag === 'self' ? true : false}
                    meta={reportData.meta}
                  />
                  <HideableSection
                    nameToDisplay="Ownership History"
                    nameToRecord="ownership_history"
                    id="vehicle-ownership-history"
                  >
                    <VehicleOwnershipHistorySection
                      vehicleOwnershipHistories={
                        reportData.automobiles[0]?.ownership_history
                      }
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Title Summary"
                    nameToRecord="title_summary"
                    id="vehicle-title-summary"
                  >
                    <VehicleTitleSummarySection
                      vehicleTitleRegistrationsList={
                        reportData?.automobiles?.[0]?.title_registrations
                      }
                      vehicleTitleBrandsList={
                        reportData?.automobiles?.[0]?.title_checks
                      }
                      vehicleLiensList={reportData?.automobiles?.[0]?.liens}
                      vehicleImpoundList={
                        reportData?.automobiles?.[0]?.impounds
                      }
                      statesContact={statesContactInfo?.states_contact}
                      nmvtisSuccess={
                        reportData?.automobiles?.[0]?.nmvtis_success || false
                      }
                      reportUpgraded={reportData.report_upgraded}
                      handleFreeUpgrade={handleFreeUpgrade}
                    />
                  </HideableSection>
                  <EtagCTA />
                  <HideableSection
                    nameToDisplay="Export Records"
                    nameToRecord="export_records"
                    id="vehicle-exports"
                  >
                    <VehicleExportsSection
                      vehicleExportList={reportData?.automobiles?.[0]?.exports}
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Theft Records"
                    nameToRecord="theft_records"
                    id="vehicle-thefts"
                  >
                    <VehicleTheftsSection
                      vehicleTheftsList={reportData.automobiles[0]?.thefts}
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Inspections"
                    nameToRecord="inspections"
                    id="vehicle-inspections"
                  >
                    <VehicleInspectionsSection
                      inspectionEvents={
                        reportData.automobiles[0]?.inspection_events
                      }
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Sale Listings"
                    nameToRecord="sales"
                    id="vehicle-sales"
                  >
                    <VehicleSalesSection
                      vehicleSalesList={reportData.automobiles[0]?.sales}
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Auctions"
                    nameToRecord="auctions"
                    id="vehicle-auctions"
                  >
                    <VehicleAuctionsSection
                      vehicleAuctionsList={reportData.automobiles[0]?.auctions}
                      vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                    />
                  </HideableSection>
                  {reportData.automobiles[0]?.detailedHistory && (
                    <HideableSection
                      nameToDisplay="Detailed History"
                      nameToRecord="detailed_history"
                      id="detailed-history"
                    >
                      <VehicleDetailedHistorySection
                        detailedHistory={
                          reportData.automobiles[0]?.detailedHistory
                        }
                        ymm={ymm}
                      />
                    </HideableSection>
                  )}
                  <HideableSection
                    nameToDisplay="Damage History"
                    nameToRecord="damage_history"
                    id="vehicle-damage-history"
                  >
                    <VehicleDamageHistorySection {...damageHistoryProps} />
                  </HideableSection>

                  <HideableSection
                    nameToDisplay="Service and Repairs"
                    nameToRecord="service_and_repairs"
                    id="vehicle-service-and-repairs"
                  >
                    <VehicleServiceRepairsSection
                      repairs={reportData.automobiles[0]?.repairs || []}
                      publicReceipts={publicReceipts}
                      isPublicReceiptsLoading={isPublicReceiptsLoading}
                      hasMoreReceipts={hasMoreReceipts}
                      isFetchingNextReceipts={isFetchingNextReceipts}
                      fetchNextReceiptsPage={fetchNextReceiptsPage}
                    />
                  </HideableSection>
                  <HideableSection
                    nameToDisplay="Maintenance Schedule"
                    nameToRecord="maintenance_schedule"
                    id="vehicle-maintenance"
                  >
                    <VehicleMaintenanceScheduleSection
                      vehicleMaintenanceSchedules={
                        reportData.automobiles[0]?.maintenances || []
                      }
                    />
                  </HideableSection>
                </>
              )}
              <HideableSection
                nameToDisplay="Recalls"
                nameToRecord="recalls"
                id="vehicle-recalls"
              >
                <VehicleRecallsSection
                  vehicleRecallList={[
                    ...(reportData.automobiles[0]?.openRecalls || []),
                    ...(isYMMSearch
                      ? reportData.automobiles[0]?.otherRecalls || []
                      : reportData.automobiles[0]?.closedRecalls || []),
                  ]}
                />
              </HideableSection>
            </>
            <>
              <HideableSection
                nameToDisplay="Market Value"
                nameToRecord="market_value"
                id="vehicle-market"
              >
                <VehicleValueSection vehicleReport={reportData} />
              </HideableSection>
              <HideableSection
                nameToDisplay="Ownership Costs"
                nameToRecord="ownership_costs"
                id="vehicle-ownership-cost"
              >
                <VehicleOwnershipCostSection
                  vehicleOwnershipCosts={
                    reportData.automobiles[0]?.ownership_costs
                  }
                  vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                />
              </HideableSection>

              <HideableSection
                nameToDisplay="Specs"
                nameToRecord="specifications"
                id="vehicle-specs"
              >
                <VehicleSpecsSection
                  vehicleSpecs={vehicleSpecs}
                  vehicleDesign={reportData.automobiles[0]?.design}
                  firstVehicleImage={reportData.automobiles[0]?.images?.[0]}
                />
              </HideableSection>
            </>
            <HideableSection
              nameToDisplay="Vehicle Safety"
              nameToRecord="vehicle_safety"
              id="vehicle-warranty"
            >
              <VehicleSafetyRatingsSection
                vehicleSafetyRatingsList={
                  reportData.automobiles[0]?.safety_ratings || []
                }
                vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                complaintsCount={data?.complaints?.length || 0}
              />
            </HideableSection>
            <HideableSection
              nameToDisplay="Warranties"
              nameToRecord="warranties"
              id="vehicle-warranty"
            >
              <VehicleWarrantySection
                vehicleWarrantyList={reportData.automobiles[0]?.warranties}
              />
            </HideableSection>
            <HideableSection
              nameToDisplay="Complaints"
              nameToRecord="complaints"
              id="vehicle-safety-complaints-section"
            >
              <VehicleSafetyComplaintsSection
                ymm={reportData.automobiles[0]?.vehicleYMM || ''}
                complaints={data?.complaints}
              />
            </HideableSection>
            {hasAllYMMPresent && (
              <>
                <CommunityBuzz
                  make={make}
                  model={model}
                  year={year}
                  trim={trim}
                  limitIntroModalToSession
                  shouldHideModal={hasSkippedCommunityBuzzModal ?? false}
                  onModalShown={handleCommunityBuzzModalShown}
                />
                <SocialSentiment
                  make={make}
                  model={model}
                  year={year}
                  trim={trim}
                />
              </>
            )}
            <NotesSection permalink={permalink} />
            <ReportRating
              permalink={permalink}
              rating={reportData.meta?.rating || null}
            />
            <VehicleReportDisclaimer />
          </Grid>
        </Grid>
        {isMobile && (
          <ReportNavigationMobileMenu
            reportType="Vehicle Report"
            headerTitle={reportTitle || reportSubtitle || ''}
            subheaderTitle={reportSubtitle || ''}
            menuItems={
              vehicleNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
            customTitleStyle={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              color: '#0A0B0D',
            }}
          />
        )}
        <DebugMenu menuItems={report.data.rawData.debug_menu} />
        {preferencesModalVisible && (
          <UserReportPreferencesModal
            sectionsToHide={sectionsToHide}
            handleOnClose={handlePreferencesModalVisibility}
          />
        )}
      </LLMProvider>
    </HiddenSectionsProvider>
  );
}
